// Base Processor class with protected fields for use in subclasses
class Processor {
  _audioContext;
  _node;
  _processorType;

  /**
   * Initializes the base Processor class with an audio context.
   * Subclasses should define the specific node type and processing behavior.
   * 
   * @param {AudioContext} audioContext - The AudioContext for managing audio operations.
   */
  constructor(audioContext) {
    this._audioContext = audioContext;
    this._node = null; // Placeholder for the main processing node, to be set in subclasses
    this._processorType = ''; // Placeholder for defining processor type in subclassesx
    this._bypass = false; // default is "not bypassed"
  }

  /**
   * Returns the main audio node of the processor.
   * @returns {AudioNode} The processing node.
   */
  getNode() {
    return this._node;
  }

  /**
   * Returns the type of the processor.
   * @returns {string} The processor type.
   */
  getProcessorType() {
    return this._processorType;
  }

  /**
   * Provides access to the audio context.
   * @returns {AudioContext} The audio context associated with this processor.
   */
  getAudioContext() {
    return this._audioContext;
  }

  // Bypass checks
  getBypass() {
    return this._bypass;
  }

  connect(target) {
    if (target instanceof Processor) {
      return this._node.connect(target.getNode());
    }
    else if (target instanceof AudioNode) {
      return this._node.connect(target);
    }
    else {
      throw new Error(
        'EQProcessor.connect() expected a Processor or AudioNode.'
      );
    }
  }

  disconnect(target) {
    if (target instanceof Processor) {
      return this._node.disconnect(target.getNode());
    }
    else if (target instanceof AudioNode) {
      return this._node.disconnect(target);
    }
    else {
      throw new Error(
        'EQProcessor.disconnect() expected a Processor or AudioNode.'
      );
    }
  }

  disconnectAll() {
    this._node.disconnect();
  }

  update(config) {
    this._node = config
  }
}

// EQProcessor class extending Processor for equalization, using a BiquadFilterNode
class EQProcessor extends Processor {
  /**
   * Creates an EQProcessor instance with specific filter properties.
   * Initializes a BiquadFilterNode as the main processing node for equalization.
   * 
   * @param {AudioContext} audioContext - The AudioContext for managing audio operations.
   * @param {string} type - The filter type (e.g., 'peaking', 'lowpass').
   * @param {number} frequency - The central frequency for the filter.
   * @param {number} gain - The gain value for the filter.
   * @param {number} Q - The Q factor (bandwidth) for the filter.
   * @param {number} detune - The detune value for fine-tuning the filter frequency.
   */
  constructor(audioContext, type = 'peaking', frequency = 350, gain = 0, Q = 1, detune = 0, bypass = false) {
    super(audioContext);
    this._processorType = 'eq';

    // Initialize a BiquadFilterNode with specified properties for EQ
    this._node = this.getAudioContext().createBiquadFilter();
    this._node.type = type;
    this._node.frequency.value = frequency;
    this._node.gain.value = gain;
    this._node.Q.value = Q;
    this._node.detune.value = detune;
    this._bypass = bypass;
  }

  getType() {
    return this._node.type;
  }

  getFrequency() {
    return this._node.frequency;
  }

  getGain() {
    return this._node.gain;
  }

  getQ() {
    return this._node.Q;
  }

  getDetune() {
    return this._node.detune;
  }
  
  getBypass() {
    return this._bypass;
  }
  /**
   * Updates the frequency parameter of the filter node.
   * @param {number} frequency - The new frequency value.
   */
  updateFreq(frequency) {
    this._node.frequency.value = frequency;
  }

  /**
   * Updates the gain parameter of the filter node.
   * @param {number} gain - The new gain value.
   */
  updateGain(gain) {
    this._node.gain.value = gain;
  }

  /**
   * Updates the Q factor (bandwidth) parameter of the filter node.
   * @param {number} Q - The new Q factor value.
   */
  updateQ(Q) {
    this._node.Q.value = Q;
  }

  /**
   * Updates the detune parameter for the filter node, for fine frequency adjustment.
   * @param {number} detune - The new detune value.
   */
  updateDetune(detune) {
    this._node.detune.value = detune;
  }

  /**
   * Updates the filter type of the node.
   * @param {string} type - The new filter type (e.g., 'peaking', 'lowpass').
   */
  updateType(type) {
    this._node.type = type;
  }

  updateBypass(bypass) {
    this._bypass = bypass;
  }

  /**
   * Applies new filter parameters to the node.
   * Accepts an object containing any of the filter parameters: type, frequency, gain, Q, and detune.
   * 
   * @param {Object} params - An object with filter parameters to update.
   * @param {string} [params.type] - New filter type.
   * @param {number} [params.frequency] - New frequency value.
   * @param {number} [params.gain] - New gain value.
   * @param {number} [params.Q] - New Q factor.
   * @param {number} [params.detune] - New detune value.
   */
  update({ type, frequency, gain, Q, detune, bypass}) {
    if (type !== undefined) this.updateType(type);
    if (frequency !== undefined) this.updateFreq(frequency);
    if (gain !== undefined) this.updateGain(gain);
    if (Q !== undefined) this.updateQ(Q);
    if (detune !== undefined) this.updateDetune(detune);
    if (bypass !== undefined) this.updateBypass(bypass);
  }
}

export default EQProcessor;
export { Processor, EQProcessor };
